import React, { useState, useCallback } from "react";
import { useHistory } from "react-router-dom";
import PaymentMethodSelection from "./components/PaymentMethodSelection";
import SavedPaymentMethods from "./components/SavedPaymentMethods";
import BankAccountForm from "./components/BankAccountForm";
import MpesaPaybillForm from "./components/MpesaPaybillForm";
import MpesaTillForm from "./components/MpesaTillForm";
import { CheckCircle, ArrowLeft } from "lucide-react";

import * as localService from "../../../services/localService";
import logger from "../../../utils/logger";


const HostPaymentSetup = () => {
  const history = useHistory();
  const [currentStep, setCurrentStep] = useState("saved"); // 'saved', 'select', 'bank', 'paybill', 'till', 'complete'
  const [selectedMethod, setSelectedMethod] = useState("");
  const [editingPaymentMethod, setEditingPaymentMethod] = useState(null);

  // Role-based permissions
  const user = localService.getUser();
  const userRole = user?.organizationRole?.code || user?.role || "";
  const canEditPayments = ["ROLE_HOST", "ROLE_SUBSCRIBER_HOST"].includes(userRole);

  const handleAddNew = () => {
    if (!canEditPayments) return;
    setCurrentStep("select");
  };

  const handleEditPayment = useCallback(paymentMethod => {
    logger.log("=== HANDLE EDIT PAYMENT CALLED ===");
    logger.log("paymentMethod:", paymentMethod);
    logger.log("paymentMethod.type:", paymentMethod.type);
    logger.log("paymentMethod.id:", paymentMethod.id);

    // Store the payment method data for editing
    setEditingPaymentMethod(paymentMethod);
    logger.log("Set editingPaymentMethod to:", paymentMethod);

    // Set the selected method and go to the appropriate form
    if (paymentMethod.type === "bank") {
      logger.log("Setting currentStep to: bank");
      setCurrentStep("bank");
    } else if (paymentMethod.type === "paybill") {
      logger.log("Setting currentStep to: paybill");
      setCurrentStep("paybill");
    } else if (paymentMethod.type === "till") {
      logger.log("Setting currentStep to: till");
      setCurrentStep("till");
    }
  }, []);

  const handleMethodSelect = method => {
    setSelectedMethod(method);
    if (method === "bank") {
      setCurrentStep("bank");
    } else if (method === "paybill") {
      setCurrentStep("paybill");
    } else if (method === "till") {
      setCurrentStep("till");
    }
  };

  const handlePrevious = () => {
    if (currentStep === "bank" || currentStep === "paybill" || currentStep === "till") {
      // If we're editing a payment method, go back to saved methods
      if (editingPaymentMethod) {
        setCurrentStep("saved");
        setEditingPaymentMethod(null);
      } else {
        // If we're adding new, go back to selection
        setCurrentStep("select");
      }
    } else if (currentStep === "select") {
      setCurrentStep("saved");
    }
  };

  const handleComplete = () => {
    setEditingPaymentMethod(null); // Clear editing state
    setCurrentStep("saved"); // Go back to saved payment methods instead of complete
  };

  const handleBackToSettings = () => {
    history.push("/host/settings");
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case "saved":
        return <SavedPaymentMethods onAddNew={handleAddNew} onEdit={handleEditPayment} canEdit={canEditPayments} />;
      case "select":
        return <PaymentMethodSelection onMethodSelect={handleMethodSelect} selectedMethod={selectedMethod} />;
      case "bank":
        return (
          <BankAccountForm
            onPrevious={handlePrevious}
            onComplete={handleComplete}
            existingData={editingPaymentMethod}
          />
        );
      case "paybill":
        return (
          <MpesaPaybillForm
            onPrevious={handlePrevious}
            onComplete={handleComplete}
            existingData={editingPaymentMethod}
          />
        );
      case "till":
        return (
          <MpesaTillForm onPrevious={handlePrevious} onComplete={handleComplete} existingData={editingPaymentMethod} />
        );
      case "complete":
        return (
          <div className="bg-white rounded-lg shadow-sm border">
            <div className="px-6 py-4 border-b">
              <h1 className="text-xl font-semibold">Payment Setup Complete</h1>
              <div className="w-full bg-gray-200 h-1 mt-2">
                <div className="bg-green-500 h-1 w-full"></div>
              </div>
            </div>

            <div className="p-6 text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-xl font-semibold mb-2">Setup Complete!</h2>
              <p className="text-gray-600 mb-6">
                Your payment method has been successfully configured. You can now receive payments from your bookings.
              </p>
              <button
                onClick={handleBackToSettings}
                className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 flex items-center mx-auto"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Settings
              </button>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen p-4">
      <div className="max-w-2xl mx-auto">{renderCurrentStep()}</div>
    </div>
  );
};

export default HostPaymentSetup;
